import type {CSSProperties} from 'react'
import {useEffect, useRef, useState} from 'react'
import {t} from '@github-ui/swp-core/lib/utils/localization'
import {Image, MinimalVideoPlayer, Text} from '@primer/react-brand/esm'
import {getMediaSizeVars} from '../../_utils/media-size-vars'
import styles from './HeroSection.module.css'
import {DESKTOP_MEDIA_QUERY, isImage, type SlideMedia} from './hero-slide-media'

type Props = {
  media: SlideMedia
  title?: string
  descriptionId: string
  isSelected?: boolean
}

export function HeroSlideMedia({media, title, descriptionId, isSelected = true}: Props) {
  const {fallback, desktop} = media
  const file = fallback.fields.asset.fields.file
  if (!file) return null

  const description = fallback.fields.description ?? fallback.fields.asset.fields.description
  const imageDimensions = file.details?.image
  const width = fallback.fields.width ?? imageDimensions?.width
  const height = fallback.fields.height ?? imageDimensions?.height
  const desktopUrl = desktop?.fields.asset.fields.file?.url
  // Unitless sizes for CSS (aspect-ratio), since the <picture>/<video> only carry the default asset's size.
  const sizeVars = getMediaSizeVars(fallback, desktop)

  if (isImage(fallback)) {
    return desktopUrl ? (
      <Image
        as="picture"
        src={file.url}
        sources={[{srcset: desktopUrl, media: DESKTOP_MEDIA_QUERY}]}
        width={width}
        height={height}
        alt={description ?? ''}
        style={sizeVars}
      />
    ) : (
      <Image src={file.url} width={width} height={height} alt={description ?? ''} />
    )
  }

  const describedBy = description ? descriptionId : undefined

  return (
    <>
      {description ? (
        <Text className="sr-only" id={descriptionId}>
          {description}
        </Text>
      ) : null}
      {/* `key`: the browser only picks a <source> when it loads, so remount if the assets change. */}
      <HeroSlideVideo
        key={`${file.url}|${desktopUrl ?? ''}`}
        media={media}
        title={title ?? t('Video')}
        describedBy={describedBy}
        isSelected={isSelected}
        sizeVars={sizeVars}
      />
    </>
  )
}

type VideoProps = {
  media: SlideMedia
  title: string
  describedBy?: string
  isSelected: boolean
  sizeVars: CSSProperties
}

function HeroSlideVideo({media, title, describedBy, isSelected, sizeVars}: VideoProps) {
  const {fallback, desktop, poster} = media
  const file = fallback.fields.asset.fields.file
  const desktopUrl = desktop?.fields.asset.fields.file?.url
  const videoRef = useRef<HTMLVideoElement>(null)
  const [hasPlayed, setHasPlayed] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    // The video can start playing before hydration, so its `playing` event would be missed.

    if (video && !video.paused && video.readyState > HTMLMediaElement.HAVE_CURRENT_DATA) setHasPlayed(true)
  }, [])

  // The player pauses while its slide is hidden and resumes where it left off; restart from the beginning instead.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Syncs the video element, which is outside React, with the selected tab.
    if (isSelected && videoRef.current) videoRef.current.currentTime = 0
  }, [isSelected])

  const player = (
    <MinimalVideoPlayer
      ref={videoRef}
      width={fallback.fields.width}
      height={fallback.fields.height}
      title={title}
      aria-describedby={describedBy}
      onPlaying={() => setHasPlayed(true)}
      style={sizeVars}
    >
      {desktopUrl ? <source src={desktopUrl} media={DESKTOP_MEDIA_QUERY} /> : null}
      <source src={file?.url} />
    </MinimalVideoPlayer>
  )

  const posterUrl = poster?.fallback.fields.asset.fields.file?.url
  if (!poster || !posterUrl) return player

  const posterDesktopUrl = poster.desktop?.fields.asset.fields.file?.url

  return (
    <div className={styles.HeroSlideVideo} data-poster-hidden={hasPlayed}>
      {player}
      {/* Decorative: it only covers the video until it plays. */}
      <Image
        as="picture"
        src={posterUrl}
        sources={posterDesktopUrl ? [{srcset: posterDesktopUrl, media: DESKTOP_MEDIA_QUERY}] : undefined}
        alt=""
        className={styles.HeroSlidePoster}
        loading={isSelected ? 'eager' : 'lazy'}
        fetchPriority={isSelected ? 'high' : 'auto'}
      />
    </div>
  )
}
